import React, { useState } from 'react';
import { apiService } from '../services/api';
import './PublisherEventReport.css';

interface EventData {
  id: number;
  date_time: string;
  goal_id: number;
  goal_name: string;
  offer_name: string;
  affiliate_id: number;
  sub_affiliate_id: string;
  click_id: string;
}

type SortField = 'date_time' | 'goal_id' | 'goal_name' | 'offer_name' | 'affiliate_id' | 'sub_affiliate_id' | 'click_id';
type SortOrder = 'asc' | 'desc';

interface PublisherEventReportProps {
  publisherIdOverride?: string;
}

const PublisherEventReport: React.FC<PublisherEventReportProps> = ({ publisherIdOverride }) => {
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const [fromDate, setFromDate] = useState<string>(getTodayDate());
  const [toDate, setToDate] = useState<string>(getTodayDate());
  const [eventData, setEventData] = useState<EventData[]>([]);
  const [filteredData, setFilteredData] = useState<EventData[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('date_time');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!fromDate || !toDate) {
      alert('Please select both From Date and To Date');
      return;
    }

    if (new Date(fromDate) > new Date(toDate)) {
      alert('From Date cannot be after To Date');
      return;
    }

    try {
      setLoading(true);
      const publisherId = publisherIdOverride || localStorage.getItem('publisherId');

      if (!publisherId) {
        alert('Publisher ID not found. Please login again.');
        return;
      }

      const response = await apiService.getPublisherEventReport(
        parseInt(publisherId),
        {
          startDate: fromDate,
          endDate: toDate
        }
      );

      setEventData(response.events || []);
      setFilteredData(response.events || []);
      setHasSearched(true);
    } catch (error: any) {
      console.error('Failed to fetch event report:', error);
      alert('Failed to load event report');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    if (!value.trim()) {
      setFilteredData(eventData);
      return;
    }

    const filtered = eventData.filter(event =>
      event.goal_name.toLowerCase().includes(value.toLowerCase()) ||
      event.offer_name.toLowerCase().includes(value.toLowerCase()) ||
      event.click_id.toLowerCase().includes(value.toLowerCase()) ||
      event.sub_affiliate_id.toLowerCase().includes(value.toLowerCase()) ||
      event.goal_id.toString().includes(value) ||
      event.affiliate_id.toString().includes(value)
    );

    setFilteredData(filtered);
  };

  const handleSort = (field: SortField) => {
    const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(newOrder);

    const sorted = [...filteredData].sort((a, b) => {
      let aVal = a[field];
      let bVal = b[field];

      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (aVal < bVal) return newOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return newOrder === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredData(sorted);
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return '⇅';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const downloadCSV = () => {
    if (filteredData.length === 0) {
      alert('No data to download');
      return;
    }

    const headers = ['Date/Time', 'Goal ID', 'Goal Name', 'Offer', 'Affiliate ID', 'Sub Affiliate ID', 'Click ID'];
    const csvContent = [
      headers.join(','),
      ...filteredData.map(event =>
        [
          event.date_time,
          event.goal_id,
          `"${event.goal_name}"`,
          `"${event.offer_name}"`,
          event.affiliate_id,
          event.sub_affiliate_id,
          event.click_id
        ].join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `event-report-${fromDate}-to-${toDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const downloadExcel = () => {
    alert('Excel download will be implemented with a library like xlsx');
  };

  const downloadPDF = () => {
    alert('PDF download will be implemented with a library like jspdf');
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  return (
    <div className="publisher-event-report">
      <div className="report-header">
        <h1>Event Report</h1>
      </div>

      <div className="date-filter-section">
        <form onSubmit={handleSubmit} className="date-filter-form">
          <div className="date-inputs">
            <div className="form-group">
              <label>From Date</label>
              <input
                type="date"
                className="date-input"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>To Date</label>
              <input
                type="date"
                className="date-input"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Loading...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>

      {hasSearched && (
        <>
          <div className="actions-bar">
            <div className="download-options">
              <button className="download-btn csv-btn" onClick={downloadCSV}>
                📄 CSV
              </button>
              <button className="download-btn excel-btn" onClick={downloadExcel}>
                📊 Excel
              </button>
              <button className="download-btn pdf-btn" onClick={downloadPDF}>
                📕 PDF
              </button>
            </div>
            <div className="search-bar">
              <input
                type="text"
                className="search-input"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="report-table-section">
            {loading ? (
              <div className="loading-state">Loading event data...</div>
            ) : filteredData.length === 0 ? (
              <div className="empty-state">
                <p>No events found for the selected date range</p>
              </div>
            ) : (
              <div className="table-container">
                <table className="event-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('date_time')} className="sortable">
                        Date/Time <span className="sort-icon">{getSortIcon('date_time')}</span>
                      </th>
                      <th onClick={() => handleSort('goal_id')} className="sortable">
                        Goal ID <span className="sort-icon">{getSortIcon('goal_id')}</span>
                      </th>
                      <th onClick={() => handleSort('goal_name')} className="sortable">
                        Goal Name <span className="sort-icon">{getSortIcon('goal_name')}</span>
                      </th>
                      <th onClick={() => handleSort('offer_name')} className="sortable">
                        Offer <span className="sort-icon">{getSortIcon('offer_name')}</span>
                      </th>
                      <th onClick={() => handleSort('affiliate_id')} className="sortable">
                        Affiliate ID <span className="sort-icon">{getSortIcon('affiliate_id')}</span>
                      </th>
                      <th onClick={() => handleSort('sub_affiliate_id')} className="sortable">
                        Sub Affiliate ID <span className="sort-icon">{getSortIcon('sub_affiliate_id')}</span>
                      </th>
                      <th onClick={() => handleSort('click_id')} className="sortable">
                        Click ID <span className="sort-icon">{getSortIcon('click_id')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((event) => (
                      <tr key={event.id}>
                        <td>{formatDateTime(event.date_time)}</td>
                        <td>{event.goal_id}</td>
                        <td>{event.goal_name}</td>
                        <td>{event.offer_name}</td>
                        <td>{event.affiliate_id}</td>
                        <td>{event.sub_affiliate_id || '-'}</td>
                        <td className="click-id">{event.click_id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="table-footer">
                  <p>Showing {filteredData.length} event(s)</p>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PublisherEventReport;
